/** User-facing page i18n extension (en) */
export default {
  router: {
    unnamed: 'Untitled page',
    login: 'Sign in',
    register: 'Create account',
    forgotPassword: 'Forgot password',
    resetPassword: 'Reset password',
    activate: 'Activate account',
    dashboard: 'Dashboard',
    targetDetail: 'Task details',
    targetReportDetail: 'Target report',
    hallucination: 'Hallucination check',
    interactiveEdit: 'Interactive edit',
    intelligenceSubscription: 'Intelligence subscription',
    intelligenceFeed: 'Intelligence updates',
    targetChat: 'Task chat',
    targetAnalysisV2: 'Task analysis',
    quickTools: 'Quick tools',
    quickToolsCreateTemplate: 'New batch template',
    quickToolsEditExpert: 'Edit expert template',
    quickToolsEditQuick: 'Edit quick template',
    outlineTemplates: 'Outline templates',
    knowledgeMap: 'Knowledge map',
    knowledgeMapSubscription: 'Map subscription settings',
    knowledgeMapSubscriptionFeed: 'Map subscription updates',
    knowledgeBase: 'Knowledge base',
    userManual: 'User Guide',
    profile: 'Profile',
    preferenceStats: 'My usage stats',
    preferenceCenter: 'My preference profile',
    feedback: 'Feedback',
    account: 'Account',
    inviteCodeManagement: 'Invite codes',
    rechargeManagement: 'Recharge',
    paymentCenter: 'Payment center',
    membership: 'Membership',
    notifications: 'Notifications'
  },
  onboarding: {
    stepProgress: 'Step {current} / {total}',
    skip: 'Skip',
    next: 'Next',
    finish: 'Finish',
    steps: {
      modesIntro: { title: 'Three chat modes', desc: 'epiXora offers Quick, Expert, and Write modes. Switch by task complexity. We will introduce each mode next.' },
      quickMode: { title: 'Quick mode', desc: 'Break down topics and generate structured questions to grasp direction and key facts fast.' },
      expertMode: { title: 'Expert mode', desc: 'For in-depth research with a full analysis workflow and report generation.' },
      writeMode: { title: 'Write mode', desc: 'Focus on drafting and polishing content from a confirmed topic.' },
      home: { title: 'Home', desc: 'Return here anytime to start a new research task in the main workspace.' },
      knowledgeBase: { title: 'Knowledge base', desc: 'Manage literature and files: folders, PDF/DOCX upload, search; pick files cited in chat.' },
      knowledgeMap: { title: 'Knowledge map', desc: 'Turn research into a structured knowledge network for review and expansion.' },
      quickTools: { title: 'Quick tools', desc: 'Batch templates for multi-topic research with tabular results and reusable template management.' },
      notifications: { title: 'Notifications', desc: 'Task updates, account changes, and system notices in one place.' },
      profile: { title: 'Profile', desc: 'Account, membership, help, and feedback entry points.' }
    }
  },
  preference: {
    center: {
      title: 'My preference profile',
      wizardTip: '3-step preference setup',
      wizard: 'Preference guide',
      statsTip: 'View my usage statistics',
      stats: 'Usage stats',
      reanalyzeTip: 'Re-analyze recent chats and reports to refresh what AI knows about you',
      reanalyze: 'Re-analyze my preferences',
      basicProfile: 'Basic profile',
      unsavedChanges: 'Unsaved changes',
      save: 'Save changes',
      reset: 'Discard',
      professionalLevel: 'Expertise level',
      levelBeginner: 'Beginner',
      levelIntermediate: 'Intermediate',
      levelExpert: 'Expert',
      terminologyLevel: 'Terminology',
      termBasic: 'Basic',
      termProfessional: 'Professional',
      termAcademic: 'Academic',
      chatStyle: 'Chat style',
      styleCasual: 'Casual',
      styleProfessional: 'Professional',
      styleAcademic: 'Academic',
      responseLength: 'Response length',
      lengthConcise: 'Concise',
      lengthModerate: 'Moderate',
      lengthDetailed: 'Detailed',
      reportDepth: 'Report depth',
      depthBrief: 'Brief',
      depthStandard: 'Standard',
      depthDetailed: 'Detailed',
      reportStyle: 'Report style',
      styleTechnical: 'Technical',
      styleBusiness: 'Business',
      styleGeneral: 'General',
      includeReferences: 'Include references',
      includeCharts: 'Include charts',
      autoLearn: 'Auto-learn preferences',
      autoLearnTip: 'When off, the system stops learning from your conversations',
      noProfile: 'No profile data yet',
      focusAreas: 'Focus areas',
      focusAreasTip: 'These tags are prioritized when generating reports and answers',
      interestedTargets: 'Targets of interest',
      addTarget: 'Add target',
      interestedDiseases: 'Diseases of interest',
      addDisease: 'Add disease',
      interestedDrugTypes: 'Drug types of interest',
      addDrugType: 'Add drug type',
      memories: 'My memories',
      memoryCount: '{count} items',
      allTypes: 'All types',
      addMemory: 'Add memory',
      noMemories: 'No memories yet. Re-analyze preferences or add manually.',
      pinned: 'Pinned',
      unpin: 'Unpin',
      pin: 'Pin (won’t be auto-cleaned)',
      deleteMemoryConfirm: 'Delete this memory?',
      confidence: 'Confidence {value}',
      confidenceLabel: 'Confidence',
      weight: 'Weight {value}',
      usageCount: 'Used {count} times',
      source: 'Source {value}',
      history: 'Preference analysis history',
      historySummary: 'Messages {chat} · Reports {reports} · Feedback {feedbacks}',
      historyMemories: 'Created {created} · Updated {updated}',
      noHistory: 'No analysis history',
      aiView: 'How AI sees me',
      copyPrompt: 'Copy prompt',
      tabChat: 'Q&A',
      tabReport: 'Report generation',
      noContext: 'No context yet. Complete your profile or re-analyze.',
      relevantMemories: 'Related memories (Top {count})',
      wizardWelcome: 'Welcome — get to know you in 3 steps',
      wizardStepBasic: 'Basic profile',
      wizardStepFocus: 'Focus areas',
      wizardStepStyle: 'Style preferences',
      wizardStep1Desc: 'Expertise and terminology affect answer depth and wording.',
      saved: 'Preferences saved',
      saveFailed: 'Save failed',
      extractStarted: 'Re-analysis started',
      extractFailed: 'Analysis failed',
      extractFailedWithReason: 'Analysis failed: {reason}',
      wizardDisease: {
        lung: 'Lung cancer',
        breast: 'Breast cancer',
        colorectal: 'Colorectal cancer',
        hematologic: 'Hematologic malignancies',
        autoimmune: 'Autoimmune diseases',
        neurodegenerative: 'Neurodegenerative diseases',
        metabolic: 'Metabolic diseases'
      },
      wizardDrug: {
        smallMolecule: 'Small molecule',
        monoclonal: 'Monoclonal antibody',
        bispecific: 'Bispecific antibody',
        adc: 'ADC',
        protac: 'PROTAC',
        cellTherapy: 'Cell therapy',
        geneTherapy: 'Gene therapy',
        mrna: 'mRNA'
      },
      promptCopied: 'Prompt copied',
      maxTags: 'At most {max} items',
      maxSelect: 'Select at most {max} items',
      maxTagsKeep: 'Keep at most {max} tags',
      memoryTypeOutline: 'Outline preference',
      memoryTypeQuestion: 'Question pattern',
      memoryTypeFocus: 'Focus area',
      memoryTypeCommunication: 'Communication style',
      memoryTypeExplicit: 'Explicit preference',
      memoryTypeNegative: 'Dislikes',
      memoryTypeProfessional: 'Professional profile',
      memoryTypeReport: 'Report preference',
      memoryTypeOther: 'Other',
      sourceChat: 'Chat',
      sourceReport: 'Report',
      sourceFeedback: 'Feedback',
      sourceManual: 'Manual',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      statusProcessing: 'Processing',
      statusPending: 'Pending',
      extractionScheduled: 'Scheduled',
      extractionTriggered: 'Triggered',
      extractionManual: 'Manual',
      wizardLevelBeginner: 'Beginner · popular science',
      wizardLevelIntermediate: 'Intermediate · balanced',
      wizardLevelExpert: 'Expert · in-depth',
      wizardStep2Desc: 'Select areas you care about; AI will prioritize them in reports and answers. You can change these anytime on the main page.',
      wizardStep3Desc: 'Finally, choose your preferred answer and report style.',
      wizardCommonTargets: 'Common targets (multi-select)',
      wizardDiseaseAreas: 'Disease areas (multi-select)',
      wizardDrugTypes: 'Drug types (multi-select)',
      wizardCustomTargetPlaceholder: 'Custom target, press Enter to add',
      wizardCustomDiseasePlaceholder: 'Custom disease, press Enter to add',
      wizardReferences: 'References',
      wizardCharts: 'Charts',
      skipWizard: 'Maybe later',
      prevStep: 'Previous',
      nextStep: 'Next',
      finishAndSave: 'Finish & save',
      wizardSaved: 'Preferences saved — AI will answer in this style right away',
      newMemoryDialog: 'Add preference memory',
      memoryType: 'Memory type',
      memoryKey: 'Memory key',
      memoryValue: 'Memory value',
      memoryKeyPlaceholder: 'e.g. prefer_clinical_focus',
      memoryValuePlaceholder: 'Describe this preference in one or two sentences',
      isPinned: 'Pin memory',
      isPinnedTip: 'Pinned memories are not auto-cleaned or decayed',
      pleaseSelect: 'Please select',
      loadProfileFailed: 'Failed to load preference profile',
      loadMemoriesFailed: 'Failed to load memories',
      saveFailedWithReason: 'Save failed: {reason}',
      reanalyzeConfirm: 'This will re-learn preferences from your recent chats and reports and may take tens of seconds. Continue?',
      reanalyzeTitle: 'Re-analyze preferences',
      start: 'Start',
      extractComplete: 'Analysis done: {created} created · {updated} updated',
      extractNotRun: 'Analysis was not run',
      pinnedSuccess: 'Pinned',
      unpinnedSuccess: 'Unpinned',
      deleted: 'Deleted',
      deleteFailed: 'Delete failed',
      fillMemoryComplete: 'Please fill in memory key and value',
      addSuccess: 'Added successfully',
      addFailedWithReason: 'Add failed: {reason}',
      noPromptToCopy: 'No prompt to copy',
      copiedToClipboard: 'Copied to clipboard',
      copyFailed: 'Copy failed — select text manually'
    },
    stats: {
      title: 'My usage statistics',
      gotoCenter: 'Back to preference profile',
      preferenceCenter: 'My preference profile',
      range: 'Date range',
      last7: 'Last 7 days',
      last30: 'Last 30 days',
      last90: 'Last 90 days',
      activeHours: 'Most active hours',
      basedOnDays: 'Based on last {d} days',
      noActivity: 'No activity yet',
      focusCloud: 'Research focus cloud',
      focusCloudTip: 'From feature usage and your focus tags',
      noFocus: 'No focus data. Complete your preference profile.',
      commonPaths: 'Common visit paths',
      path: 'Path',
      count: 'Count',
      noPath: 'No visit paths',
      debugDetail: 'Debug: raw API / button usage',
      apiTop: 'Top 10 API calls',
      apiName: 'API name',
      btnTop: 'Top 10 button clicks',
      btnName: 'Button name',
      noData: 'No data',
      avgSession: 'Avg. session length',
      pageVisits: 'Page visits',
      reports: 'Reports generated',
      chats: 'Chat sessions',
      loadFailed: 'Failed to load statistics'
    }
  },
  profile: {
    myPreferenceCenter: 'My preference profile'
  },
  registerMarketing: {
    title: 'Create your account',
    subtitle: 'Start your quick research journey',
    heroTitle: 'Explore your\npersonal\nresearch intelligence hub',
    heroDesc: 'Cover target research, competitive intel, R&D, and market trends with tailored reports beyond manual search limits.',
    phoneRegistered: 'This phone number is already registered. You can sign in directly.'
  },
  activate: {
    title: 'Activate account',
    loading: 'Activating your account, please wait...',
    successTitle: 'Activation successful!',
    successSub: 'Your account is active. You can sign in now.',
    goLogin: 'Go to sign in',
    errorTitle: 'Activation failed',
    errorSub: 'The activation link is invalid or expired. Contact your admin for a new email.',
    missingToken: 'Missing activation token. Check that the email link is complete.',
    resendEmail: 'Resend activation email'
  },
  inviteCode: {
    back: 'Back',
    title: 'Invite codes',
    description: 'Share your invite code and earn rewards when friends register',
    successInvites: 'Successful invites',
    totalRewards: 'Total rewards (X-Coin)',
    pendingRewards: 'Pending rewards',
    totalInvites: 'Total invites',
    myCode: 'My invite code',
    copyLink: 'Copy invite link',
    codeLabel: 'Code:',
    linkLabel: 'Invite link:',
    loading: 'Loading...',
    scanRegister: 'Scan to register',
    shareWechat: 'Share to WeChat',
    wechatShort: 'WeChat',
    copySuccess: 'Copied to clipboard',
    copyFailed: 'Copy failed'
  },
  rechargePage: {
    back: 'Back',
    title: 'Recharge',
    description: 'Top up X-Coin for more services. First recharge may include invite rewards.',
    balanceTitle: 'Account balance',
    rechargeNow: 'Recharge now',
    currentBalance: 'Current balance',
    totalRecharged: 'Total recharged',
    yuan: 'CNY',
    rechargeCount: 'Recharge count',
    times: 'times',
    firstRecharge: 'First recharge',
    recordsTitle: 'Recharge history',
    orderPlaceholder: 'Order ID',
    filterStatus: 'Filter status',
    allRecords: 'All records',
    statusCompleted: 'Paid',
    statusPending: 'Pending',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
    query: 'Search',
    emptyRecords: 'No recharge records',
    amount: 'Amount',
    xcoin: 'X-Coin received',
    paymentMethod: 'Payment method',
    paymentStatus: 'Status',
    firstRechargeCol: 'First recharge',
    firstBadge: 'First'
  },
  progressChannels: {
    // Loom v2 deep-research channels — schema in
    // frontend/src/utils/deepResearchChannels.js, backend specs in
    // backend/app/workflows/deep_research/progress_channels.py.
    // Backend writes already-localized step_message; these templates serve
    // as locale fallback (e.g. historical rows missing step_message).
    'dr.planning.completed': 'Research planning complete',
    'dr.knowledge.search_started': 'Searching: {query}',
    'dr.knowledge.search_completed': 'Collected {total_count} items',
    'dr.knowledge.completed': 'Data collection complete',
    'dr.synthesis.toe_started': 'Building knowledge graph',
    'dr.synthesis.toe_stage_completed': 'Graph stage: {stage}',
    'dr.synthesis.toe_completed': 'Knowledge graph complete',
    'dr.synthesis.report_started': 'Writing report',
    'dr.synthesis.report_outline_written': 'Outline: {title}',
    'dr.synthesis.report_section_written': 'Section: {section_title}',
    'dr.synthesis.report_abstract_written': 'Abstract generated',
    'dr.synthesis.report_conclusion_written': 'Conclusion generated',
    'dr.synthesis.report_completed': 'Report writing complete',
    'dr.run.completed': 'Report generation complete',
    'dr.translation.started': 'Translating',
    'dr.translation.completed': 'Translation complete',
    'dr.packaging.completed': 'Report packaged',
    'ts.planning.completed': 'Research planning complete',
    'ts.collection.started': 'Starting data retrieval',
    'ts.collection.progress': 'Retrieving data',
    'ts.collection.completed': 'Data retrieval complete',
    'ts.writing.started': 'Writing report',
    'ts.writing.completed': 'Report writing complete',
    'ts.packaging.completed': 'Report packaged',
    'target_survey.workflow.started': 'Starting target survey workflow',
    'target_survey.started': 'Starting target survey',
    'target_survey.input': 'Starting target survey',
    'target_survey.research': 'Collecting target evidence',
    'target_survey.structured': 'Structuring evidence',
    'target_survey.report': 'Writing target report',
    'target_survey.report.draft': 'Updating report draft preview',
    'target_survey.quality': 'Quality check',
    'target_survey.completed': 'Quality check passed — report ready',
    'target_survey.trace.lane_started': 'Starting multi-dimensional research',
    'target_survey.trace.lane_progress': 'Collecting research data',
    'target_survey.trace.checkpoint_resume': 'Resuming research progress',
    'target_survey.trace.gap_backfill': 'Supplementing key evidence',
    'target_survey.trace.synthesis': 'Writing unified report',
    'target_survey.trace.quality_check': 'Verifying citations',
    'target_survey.trace.retry': 'Network delay — retrying',
    'target_survey.trace.hermes': 'Research in progress',
    'llm_agent.task.submitted': 'Writing task submitted',
    'llm_agent.task': 'Remote task accepted',
    'llm_agent.workflow.started': 'Writing started',
    'llm_agent.tool.dispatched': 'Writing in progress',
    'llm_agent.tool.completed': 'Writing in progress',
    'llm_agent.workflow.completed': 'Writing complete',
    'hermes_writing.started': 'Hermes free-writing started',
    'hermes_writing.resume': 'Resuming Hermes free-writing',
    'hermes_writing.progress': 'Hermes research & drafting',
    'hermes_writing.tool.dispatched': 'Hermes tool call',
    'hermes_writing.persist': 'Saving Hermes draft',
    'hermes_writing.completed': 'Hermes free-writing complete'
  },
  kbFolder: {
    allLibraries: 'All libraries',
    unnamedFolder: 'Unnamed folder',
    fileCount: '{count} files',
    collapseInfo: 'Collapse file info',
    expandInfo: 'Expand file info',
    fileName: 'File name',
    fileType: 'Type',
    fileSize: 'Size',
    uploadedAt: 'Uploaded',
    actions: 'Actions',
    download: 'Download',
    rename: 'Rename',
    delete: 'Delete',
    fileInfo: 'File info',
    relatedReports: 'Related reports',
    completed: 'Completed',
    noRelatedReports: 'No related reports',
    labelName: 'Name',
    labelSize: 'Size',
    labelUploader: 'Uploader',
    emptyFiles: 'No files yet',
    notConnected: 'Not available yet',
    finishedAt: 'Completed at',
    fileContent: 'File content',
    noContentPreview: 'No content preview',
    noFileDetail: 'No file details'
  },
  hallucinationPage: {
    filterAll: 'All',
    filterPending: 'Pending',
    filterResolved: 'Resolved',
    filterDismissed: 'False positive',
    tagResolved: 'Resolved',
    tagDismissed: 'False positive',
    markDismissed: 'Mark as false positive',
    undo: 'Undo',
    restore: 'Restore',
    dismissConfirm: 'Mark this item as a false positive? It will be removed from Pending.',
    dismissTitle: 'False positive',
    dismissOk: 'Confirm',
    dismissed: 'Marked as false positive',
    noPending: 'No pending hallucination issues'
  },
  targetReportDetail: {
    archive: {
      archived: 'Archived to knowledge base',
      rearchive: 'Re-archive / attach to another folder',
      saveTitle: 'Save to knowledge base',
      selectFolder: 'Select target folder',
      quickSave: 'Quick save to knowledge base',
      createTopic: 'Quick-create research topic',
      createAndSelect: 'Create and select',
      confirmSave: 'Confirm save',
      rearchiveTitle: 'Re-archive'
    }
  },
  reportInteractiveEdit: {
    backToReport: 'Back to report',
    title: 'Interactive edit',
    unsaved: 'Unsaved',
    synced: 'Synced',
    layoutAria: 'Workspace layout',
    stop: 'Stop',
    traceTip: 'Execution trace (tools & billing summary)',
    trace: 'Trace',
    save: 'Save',
    more: 'More',
    undoAi: 'Undo AI edit',
    redoAi: 'Redo AI edit',
    versionHistory: 'Version history',
    exportMd: 'Export Markdown',
    exportWord: 'Export Word',
    exportPdf: 'Export PDF',
    reload: 'Reload',
    noPermission: 'No permission for interactive edit',
    assistant: 'AI assistant',
    assistantChatAria: 'AI assistant chat',
    selectionChars: 'Selection {count} chars',
    refreshSelection: 'Refresh selection',
    showReport: 'Show report',
    collapseReportTip: 'Collapse report — focus on chat',
    quickCommands: 'Quick commands',
    quickCommandsHint: 'Click to insert · Double-click custom to edit · “+ Custom” to add',
    clickToFill: 'Click to insert',
    dblClickToEdit: 'Click to insert · Double-click to edit',
    delete: 'Delete',
    custom: 'Custom',
    chatHistory: 'Chat history',
    messageCount: '{count} messages',
    restoredLocal: 'Local history restored',
    restoredServer: 'Server history synced',
    exitFullscreen: 'Exit fullscreen',
    fullscreenChat: 'Fullscreen chat',
    fullscreen: 'Fullscreen',
    fullscreenView: 'Fullscreen view',
    chatEmptyTitle: 'Start collaborating with the AI assistant on your report',
    quickStart: 'Quick start',
    chatEmptyTip1: 'Select text in the report and click “Refresh selection” to include it with your next instruction.',
    chatEmptyTip2: '{sendShortcut} to send; {saveShortcut} to save to server.',
    chatEmptyTip3: 'View the execution trace in the right drawer (header “Trace” or footer link); it opens automatically while generating.',
    incomplete: 'Incomplete',
    expand: 'Expand',
    collapse: 'Collapse',
    copyPlain: 'Copy plain text',
    copy: 'Copy',
    imageTag: 'Img {name}',
    charsClickExpand: '({count} chars) click to expand',
    writeToSection: 'Write to section',
    copyMarkdown: 'Copy Markdown',
    retryTurn: 'Retry this turn',
    retryTurnHint: 'Resends the same instruction, selection, and attachments',
    reasoning: 'Reasoning and calling tools…',
    selectionBannerBefore: 'Selected',
    selectionBannerAfter: 'chars — sent with your next instruction',
    clear: 'Clear',
    advancedToolsAttachments: 'Advanced · tool scope / attachments',
    toolScope: 'Tool scope',
    toolScopeStandard: 'Recommended (search + edit, default)',
    toolScopeDocumentOnly: 'Document only (no web tools)',
    toolScopeFull: 'Full tools (incl. structure DB / ADMET)',
    toolScopeHint: 'ADMET/structure DB excluded by default; choose Full for PDB, etc.',
    bodyAttachments: 'Body attachments (auto-extract)',
    uploadFile: 'Upload file',
    taskFiles: 'Task files',
    knowledgeBase: 'Knowledge base',
    cloudFiles: 'Cloud files',
    bodyAttachHint: 'PDF/Word/Excel/PPT(.pptx)/HTML/text etc. are auto-extracted; up to {max}.',
    imageAttachments: 'Image attachments (embed in report)',
    uploadImage: 'Upload image',
    taskImages: 'Task images',
    cloudImages: 'Cloud images',
    imageAttachHint: 'AI uses embed_report_images after send; or insert at cursor in the report panel. Up to {max} images.',
    targetChapters: 'Target sections (optional)',
    targetChaptersPlaceholder: 'Leave empty for AI to match relevant sections',
    targetChaptersHint: 'When set: body attachments go to those sections; images embed near matching headings.',
    promptPlaceholder: 'Describe how to edit using attachments/selection, or use quick commands; upload PDF, Word, Excel, text, etc.',
    chatFullscreenPlaceholder: 'Type your message; Ctrl/Cmd + Enter to send',
    switchModel: 'Switch model: {model}',
    modelGroupSystem: 'System recommended',
    modelGroupTier: 'Membership tier',
    modelGroupFallback: 'Other available',
    noModelsFallback: 'Model list unavailable; backend default will be used',
    send: 'Send',
    sendCommand: 'Send instruction',
    sendShortcut: 'Send',
    clearChat: 'Clear chat',
    saveToServer: 'Save to server',
    saveShortcutBar: 'Save',
    splitHandleAria: 'Drag to resize chat and report panels',
    editorAria: 'Markdown edit & preview',
    tabSource: 'Source',
    tabSplit: 'Split',
    tabPreview: 'Preview',
    showChat: 'Show chat',
    collapseChatTip: 'Collapse chat — focus on report',
    chapterIndex: 'Section index',
    insertImage: 'Insert image',
    insertPendingImages: 'Insert pending images {count}',
    syncScroll: 'Sync scroll',
    charCount: '{count} chars',
    mdEditorPlaceholder: 'Markdown body (editable)',
    traceLink: 'Trace',
    traceDrawerTitle: 'Execution trace',
    traceDrawerTip: 'Shows steps, tool calls, and billing summary for this and prior turns; opens automatically while generating.',
    inProgress: 'In progress',
    stepCount: '{count} steps',
    copyAll: 'Copy all',
    clearTrace: 'Clear',
    traceEmpty: 'Steps, tools, and billing summary appear after you send an instruction',
    backendDuration: 'Backend measured duration',
    estimatedDuration: 'Estimated from adjacent events',
    toolDataBytes: 'Tool call data size',
    taskFilesDialogTitle: 'Select from current task',
    fileName: 'File name',
    attachment: 'Attachment',
    extractable: 'Extractable',
    notExtractable: 'Not extractable',
    size: 'Size',
    status: 'Status',
    taskFilesHint: 'Only PDF, Word, Excel, PPT(.pptx), HTML, or text files; links, images, archives, legacy .ppt cannot be attached.',
    cancel: 'Cancel',
    addSelected: 'Add selected',
    promptEditorLabelPlaceholder: 'Short title for the command (≤ 24 chars)',
    promptEditorPromptPlaceholder: 'Command content (paste from input box, ≤ 4000 chars)',
    promptEditorHint: 'Stored locally, reused across tasks; syncs across tabs for the same account.',
    msgDetail: 'Message detail',
    msgTitleUser: 'My instruction',
    msgTitleAssistant: 'Assistant analysis',
    msgTitleSystem: 'System message',
    roleMe: 'Me',
    roleAssistant: 'Assistant',
    roleFusion: 'Search fusion',
    roleAttachment: 'Attachment inject',
    roleNote: 'Note',
    roleSystem: 'System',
    avatarMe: 'Me',
    avatarAi: 'AI',
    avatarNote: 'N',
    avatarSystem: 'S',
    emptyContent: '(empty)',
    versionsDrawerTitle: 'Report version history',
    versionsTip: 'Each manual save, autosave, or rollback archives a copy. Compare with current edits or restore in one click.',
    viewCompare: 'Compare',
    rollbackConfirm: 'Unsaved edits will be lost. Continue?',
    rollback: 'Restore',
    rollbackThisVersion: 'Restore this version',
    noVersions: 'No history yet; versions are archived when you save',
    versionCompareTitle: 'Compare · {versionId} ↔ current',
    versionCompare: 'Version compare',
    compareRender: 'Rendered',
    compareDiff: 'Source diff',
    compareHistoryOnly: 'History only',
    diffRemoved: '−{count} lines',
    diffAdded: '+{count} lines',
    diffSame: '{count} unchanged',
    historyVersion: 'History {versionId}',
    currentEdit: 'Current edit',
    hasUnsavedEdits: 'Unsaved edits',
    close: 'Close',
    rollbackToVersion: 'Restore to this version',
    kbFilesDialogTitle: 'Select from knowledge base',
    kbFolderPlaceholder: 'Select knowledge base folder',
    filterByFileName: 'Filter by file name',
    kbFilesHint: 'Only items linked to files records with extractable body (PDF/Word/Excel/PPT/text); links and report-only archives are not supported.',
    cloudFilesDialogTitle: 'Select from cloud drive',
    fusionWriteTitle: 'Write fusion search into report',
    fusionWriteHint: 'Inserts deduplicated Markdown block into the chosen section (no LLM turn; save when done).',
    targetSection: 'Target section',
    targetSectionPlaceholder: 'Select or type heading keyword',
    insertPosition: 'Insert position',
    afterSection: 'End of section',
    afterTitle: 'After title line',
    appendEnd: 'End of document',
    write: 'Write',
    paneBoth: 'Split',
    paneChat: 'Chat',
    paneEditor: 'Report',
    paneBothTip: 'Chat and report side by side',
    paneChatTip: 'Chat only',
    paneEditorTip: 'Report editor only',
    reportEditor: 'Report editor',
    scopeStandard: 'Recommended',
    scopeDocumentOnly: 'Doc only',
    scopeFull: 'Full',
    advancedModel: 'Model',
    advancedScope: 'Scope',
    advancedBodyAttach: 'Body attach',
    advancedTargetChapters: 'Sections',
    advancedImages: 'Images',
    selectionPreviewSuffix: '… ({count} chars total)',
    saveTypeRollback: 'Rollback',
    saveTypeManual: 'Manual save',
    saveTypeAutosave: 'Autosave',
    saveTypeHallucinationFix: 'Hallucination fix',
    saveTypeBaseline: 'Generated draft',
    saveTypeUnknown: 'Unknown',
    restoreBaselineDraft: 'Restore generated draft',
    restoreBaselineEarliest: 'Restore earliest archive',
    restoreBaselineConfirmDraft: 'Restore the generated draft? Unsaved edits will be lost.',
    restoreBaselineConfirmEarliest: 'No generated draft snapshot exists. Restore the earliest archived version ({versionId})? Unsaved edits will be lost; content may differ from the original generated report.',
    baselineTagDraft: 'Generated draft',
    baselineTagEarliest: 'Earliest archive',
    kbStatusIndexed: 'Indexed',
    kbStatusFailed: 'Failed',
    saveReasonStreaming: 'Generating — save after completion or stop.',
    saveReasonSaving: 'Saving, please wait.',
    saveReasonExporting: 'Exporting, please wait.',
    saveReasonTooShort: 'Report too short — at least 50 characters required.',
    saveReasonSynced: 'Already synced — nothing to save.',
    loadTaskFailed: 'Failed to load task',
    noMarkdownReport: 'No editable Markdown report — ensure the report has been generated',
    loadMarkdownFailed: 'Failed to load Markdown',
    downloadFailed: 'Download failed {status}',
    msg: {
      chatSyncedFromOtherWindow: 'Chat updated in another window — local history synced',
      fusionWritten: 'Fusion content written to report — review and save',
      writeFailed: 'Write failed',
      copied: 'Copied to clipboard',
      copyFailed: 'Copy failed',
      maxCustomCommands: 'At most {max} custom commands',
      customCommandSaved: 'Changes saved',
      customCommandAdded: 'Custom command added',
      deleteCustomCommand: 'Delete custom command “{label}”?',
      confirmDelete: 'Confirm delete',
      quickCommandFilled: 'Filled “{label}” — press {shortcut} or click Send instruction',
      attachmentAdded: 'Body attachment added; pick target sections or use “Edit by attachment” quick command',
      unsavedLeaveConfirm: 'You have unsaved changes. Leave anyway?',
      hint: 'Notice',
      formatFixUnavailable: 'Auto format fix unavailable — continuing with current content',
      traceCopiedAll: 'Copied {count} trace items as Markdown',
      traceCopiedOne: 'Copied one trace item as Markdown',
      clearChatConfirm: 'Clear local chat for this target? This cannot be undone.',
      clearChatTitle: 'Clear chat',
      undoSuccess: 'Restored content before this AI edit',
      redoSuccess: 'Redid AI edit',
      noImagesToEmbed: 'No images to embed',
      imagesInserted: 'Inserted {count} image(s) at cursor',
      insertImageFailed: 'Failed to insert image',
      inserted: 'Inserted: {name}',
      maxImages: 'At most {max} illustration attachments',
      imageAdded: 'Illustration added: {name}',
      maxFiles: 'At most {max} file attachments',
      fileAdded: 'Added: {name}',
      uploadFailed: 'Upload failed',
      loadTaskFilesFailed: 'Failed to load task files',
      loadCloudFilesFailed: 'Failed to load cloud files',
      loadKbListFailed: 'Failed to load knowledge base list',
      loadKbFilesFailed: 'Failed to load knowledge base files',
      kbFilesAdded: 'Added {count} knowledge base file(s)',
      kbFilesSkipped: '{count} document(s) lack file_id and cannot be attached',
      contentTooShort: 'Report too short (at least 50 characters)',
      interactiveEditFailed: 'Interactive edit failed',
      requestFailed: 'Request failed',
      versionsLoadFailed: 'Failed to load version history',
      versionContentLoadFailed: 'Failed to load version content',
      versionPreviewFailed: 'Failed to render version preview',
      rollbackOverwriteConfirm: 'Restore will overwrite current edits and discard unsaved changes. Continue?',
      confirmRollback: 'Confirm restore',
      rollingBack: 'Restoring to {versionId}…',
      rollbackSuccess: 'Restored to {versionId}',
      rollbackFailed: 'Failed to restore version',
      baselineMissing: 'No restorable version found',
      baselineRestoreSuccessDraft: 'Restored to generated draft',
      baselineRestoreSuccessEarliest: 'Restored to earliest archive',
      savedWithFormat: '(format cleanup applied)',
      saveFailed: 'Save failed',
      exportedMd: 'Exported Markdown (with format cleanup)',
      exportedWord: 'Exported Word (with format cleanup)',
      exportedPdf: 'Exported PDF (with format cleanup)',
      exportFailed: 'Export failed',
      hallucinationContextLoaded: 'Hallucination fix context loaded — send instructions and follow up; save and export when done.',
      leaveAnyway: 'Leave anyway',
      stayOnPage: 'Stay on page',
      taskImagesAdded: 'Added {count} task image(s)',
      taskFilesAdded: 'Added {count} task file(s)',
      cloudImagesAdded: 'Added {count} cloud image(s)',
      cloudFilesAdded: 'Added {count} cloud file(s)',
      clearChatConfirmBtn: 'Clear'
    },
    quickCmd: {
      terminology: { label: 'Terminology & typos' },
      compressSummary: { label: 'Compress summary' },
      clinicalTrials: { label: 'In-text clinical trials' },
      headingLevels: { label: 'Heading levels' },
      tablesLists: { label: 'Tables & lists' },
      citationsHallucination: { label: 'Citations & hallucination check' },
      keyDataCheck: { label: 'Key data verification' },
      executiveSummary: { label: 'Executive summary' },
      expandDepth: { label: 'Expand depth section' },
      editByAttachment: { label: 'Edit by attachment' }
    },
    time: {
      yesterday: 'Yesterday {time}',
      daysAgo: '{days}d ago {time}'
    }
  },
  userManual: {
    title: 'User Guide',
    searchPlaceholder: 'Search section titles or keywords…',
    noResults: 'No matching content',
    expandSidebar: 'Expand user guide sidebar',
    collapseSidebar: 'Collapse user guide sidebar',
    searchFound: '{count} matching sections',
    searchNotFound: 'No matching sections. Try another keyword.',
    bodySearchFound: '{count} matches in body',
    adminSection: 'Admin console',
    adminTag: 'Admin',
    navAria: 'User guide section navigation',
    nav: {
      overview: 'Overview',
      quickstart: 'Quick start',
      'target-analysis': 'Research tasks',
      'quick-tools': 'Quick tools',
      report: 'Reports & download',
      'interactive-edit': 'Interactive edit',
      'intelligence-subscription': 'Intelligence subscription',
      hallucination: 'Hallucination check',
      chat: 'Report chat',
      'knowledge-map': 'Knowledge map',
      'knowledge-base': 'Knowledge base',
      account: 'Account, usage & recharge',
      membership: 'Membership',
      personal: 'Profile & settings',
      'preference-center': 'Preference profile',
      feedback: 'Feedback & contact',
      faq: 'FAQ'
    },
    searchHelp: {
      overview: 'intro product overview epiXora research analysis intelligent research',
      quickstart: 'register login beginner navigation sidebar 注册 登录',
      'target-analysis': 'task new chat modes template attachments model voice outline 任务 模式',
      'quick-tools': 'batch template CSV Excel citation history topics 批量 模板',
      report: 'preview pdf word export markdown html regenerate rating 预览 下载',
      'interactive-edit': 'interactive edit fullscreen AI assistant 交互编辑',
      'intelligence-subscription': 'intelligence feishu email patrol push notification 情报 订阅',
      hallucination: 'fact check verify highlight history batch 幻觉 检测',
      chat: 'Q&A conversation history report chat 对话',
      'knowledge-map': 'wiki mind map outline 3D subscribe health heal 知识地图',
      'knowledge-base': 'rag upload local url documents knowledge base 知识库',
      account: 'xcoin balance recharge token usage invite 余额 充值',
      membership: 'tier upgrade benefits models download membership 会员',
      personal: 'avatar notification language stats profile settings 个人信息',
      'preference-center': 'preference profile memory expertise terminology 偏好画像',
      feedback: 'support ticket bug feature complaint 反馈',
      faq: 'help questions FAQ 常见问题'
    },
    adminNav: {
      'admin-user': 'Users & tenants',
      'admin-task': 'Task management & monitoring',
      'admin-finance': 'Finance & recharge',
      'admin-marketing': 'Marketing & invites',
      'admin-operation': 'Operations & content',
      'admin-system': 'System configuration',
      'admin-agent': 'Agent management',
      'admin-faq': 'Admin FAQ'
    },
    adminSearchHelp: {
      'admin-user': 'users tenants roles 用户 租户',
      'admin-task': 'celery flower worker monitoring 任务',
      'admin-finance': 'recharge payment xcoin transactions membership 财务',
      'admin-marketing': 'invite code rewards audit marketing 邀请',
      'admin-operation': 'feedback moderation notifications reviews 运营',
      'admin-system': 'models permissions env config inspection 系统配置',
      'admin-agent': 'agent studio langgraph prompt tools 智能体',
      'admin-faq': 'ops admin troubleshooting 运维'
    },
    sectionAnchor: 'Jump to this section',
    navEmpty: 'No matches',
    keywordLabel: 'Keyword:',
    contentAria: 'User guide content',
    backToTop: 'Back to user guide top',
    backToTopTitle: 'Back to top',
    headerBadge: 'User Guide',
    productTitle: 'epiXora',
    subtitle: 'Get started with Quick, Expert, and Write modes, quick tools, reports, knowledge map, preferences, and more.',
    quickResearchKicker: 'Research',
    quickResearchTitle: 'Three research modes',
    quickResearchDesc: 'Quick / Expert / Write',
    quickToolsKicker: 'Efficiency',
    quickToolsTitle: 'Batch topic research',
    quickToolsDesc: 'Templates, tables, export',
    knowledgeKicker: 'Knowledge',
    knowledgeTitle: 'Knowledge map',
    knowledgeDesc: 'Wiki, mind map, 3D graph',
    quickLinksAria: 'Quick section links',
    sectionJumpsAria: 'Jump to sections'
  },
  quickToolsCitation: {
    title: 'Source literature',
    quote: 'Quoted text',
    source: 'Source',
    context: 'Context',
    links: 'Related links'
  },
  common: {
    defaultUser: 'User'
  }
}
